/**
 * Takes a Firebase email-action request off an `/auth/action` URL before anything else in the
 * app runs. Firebase's reset emails link here with `mode`, `oobCode`, `apiKey`, `lang` and
 * `continueUrl` in the query; `oobCode` is a one-hour single-use secret. The app entry imports
 * this module right after `internalOpenToken` and for the same reason: Firebase Analytics sends
 * a page view carrying `document.location` as soon as the client module loads, and the router
 * snapshots the location when it mounts, so the query has to be gone before either happens.
 * The whole query is dropped; the original string is kept in memory so a mode this app does
 * not handle can be forwarded to Firebase's hosted handler unchanged.
 */

export const AUTH_ACTION_PATH = "/auth/action";

export type AuthActionParams = {
  /** `resetPassword`, `verifyEmail`, `recoverEmail`, `signIn`, or null when absent. */
  mode: string | null;
  oobCode: string | null;
  /** The original query string, leading `?` included, or "" when there was none. */
  query: string;
};

let captured: AuthActionParams | null = null;

const nonEmpty = (value: string | null): string | null =>
  value && value.length > 0 ? value : null;

/** The router matches paths ignoring case and trailing slashes, so the bootstrap must too, or
 * a link to `/auth/action/` renders the handler with the code still in the URL. */
const isAuthActionPath = (pathname: string): boolean =>
  pathname.replace(/\/+$/, "").toLowerCase() === AUTH_ACTION_PATH;

/**
 * The request captured at load, or null when the page was not opened on `/auth/action`.
 * Reading it does not clear it: React StrictMode runs a state initializer twice in development
 * and keeps the second result, so a one-shot value would leave the page empty on every local run.
 */
export function capturedAuthAction(): AuthActionParams | null {
  return captured;
}

/**
 * On `/auth/action`, keeps the query's request for `capturedAuthAction` and drops the whole
 * query from the URL, preserving the history state and the hash. Runs when this module is
 * imported.
 */
export function captureAuthAction(): void {
  captured = null;
  const { pathname, search, hash } = window.location;
  if (!isAuthActionPath(pathname)) return;
  const params = new URLSearchParams(search);
  captured = {
    mode: nonEmpty(params.get("mode")),
    oobCode: nonEmpty(params.get("oobCode")),
    query: search,
  };
  window.history.replaceState(window.history.state, "", pathname + hash);
}

captureAuthAction();
